import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { QuizService } from "src/app/services/api/quiz.service";
import { QuizAttempt, StudentQuestion } from "src/app/services/model/quiz.model";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { SharedModule } from "src/shared/shared.module";

/**
 * Sitting a quiz.
 *
 * One question at a time, no timer — the point of this engine is to find out
 * what a student knows, not how fast they panic. The attempt is created
 * server-side before the first question is shown, and every choice is parked
 * there too, so a refresh — or a phone locking mid-paper — resumes the same
 * questions WITH the same answers already filled in. Grading still happens once,
 * on submit.
 *
 * Nothing on this screen may hint at the answer. The payload has no
 * `is_correct` and no `correct_keys` at all — that is enforced by the API, and
 * an E2E case asserts the rendered HTML never contains either.
 */
@Component({
  selector: "app-quiz-take",
  imports: [SharedModule, CommonModule, FormsModule, RouterModule, TranslateModule],
  templateUrl: "./quiz-take.component.html",
  styleUrl: "./quiz-take.component.scss",
})
export class QuizTakeComponent {
  #quizService = inject(QuizService);
  #toastService = inject(ToastService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);

  isLoading = signal(true);
  isSubmitting = signal(false);
  attempt = signal<QuizAttempt | null>(null);
  index = signal(0);
  /** Question id → the choice key (or typed text) the student picked. */
  answers = signal<Record<number, string>>({});
  /** The review screen shown after the last question, before submitting. */
  reviewing = signal(false);

  /** Autosave is debounced: typing a short answer fires per keystroke, and one
   *  request per letter is pointless when each one sends the whole map anyway. */
  #saveTimer: ReturnType<typeof setTimeout> | null = null;
  static readonly SAVE_DEBOUNCE_MS = 600;

  questions = computed<StudentQuestion[]>(() => this.attempt()?.questions ?? []);
  current = computed<StudentQuestion | undefined>(() => this.questions()[this.index()]);
  total = computed(() => this.questions().length);

  unanswered = computed(() => {
    const given = this.answers();
    return this.questions().filter((q) => !given[q.id]).length;
  });

  ngOnInit(): void {
    const quizId = Number(this.#route.snapshot.paramMap.get("quiz_id"));
    if (!quizId) {
      this.#router.navigate(["/online-class/quiz"]);
      return;
    }

    this.#quizService.startAttempt(quizId).subscribe({
      next: (attempt) => {
        this.attempt.set(attempt);
        // Resuming: the server hands back what was already chosen, keyed by
        // question id as a string (jsonb keys always are).
        const draft = attempt.draft_answers || {};
        const restored: Record<number, string> = {};
        for (const [questionId, answer] of Object.entries(draft)) {
          if (answer !== null && answer !== undefined && answer !== "") {
            restored[Number(questionId)] = answer;
          }
        }
        this.answers.set(restored);
        this.markSeen(this.current()?.id ?? null);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.#toastService.toastError(
          err?.error?.errors || "เปิดแบบฝึกหัดนี้ไม่ได้",
        );
        this.#router.navigate(["/online-class/quiz"]);
      },
    });
  }

  isChosen(questionId: number, key: string): boolean {
    return this.answers()[questionId] === key;
  }

  choose(questionId: number, key: string): void {
    this.answers.update((current) => ({ ...current, [questionId]: key }));
    this.queueSave();
  }

  setText(questionId: number, value: string): void {
    this.answers.update((current) => ({ ...current, [questionId]: value }));
    this.queueSave();
  }

  private queueSave(): void {
    if (this.#saveTimer) clearTimeout(this.#saveTimer);
    this.#saveTimer = setTimeout(
      () => this.saveDraft(),
      QuizTakeComponent.SAVE_DEBOUNCE_MS,
    );
  }

  /** Silent on failure on purpose: a student mid-paper cannot act on "autosave
   *  failed", the answers are still on screen, and submit sends them all again
   *  anyway. The only cost of a dropped save is a resume that is out of date. */
  private saveDraft(): void {
    const attempt = this.attempt();
    if (!attempt || this.isSubmitting()) return;
    this.#quizService.saveDraftAnswers(attempt.id, this.answerPayload()).subscribe({
      error: () => {},
    });
  }

  /**
   * How long the student spent on each question, in milliseconds.
   *
   * Correct-and-quick is fluency; correct-but-laboured is a student rebuilding
   * the answer from first principles every time. Same mark, different teaching
   * problem — and the difference was being thrown away because nobody recorded
   * the clock.
   *
   * Accumulated rather than measured once: people go back, re-read, change
   * their mind, and the total time on a question is the honest figure.
   */
  #elapsed: Record<number, number> = {};
  #shownAt: number | null = null;
  #shownId: number | null = null;

  /** Bank the time spent on whatever was on screen, then start the clock on
   *  what replaced it. */
  private markSeen(questionId: number | null): void {
    const now = Date.now();
    if (this.#shownId !== null && this.#shownAt !== null) {
      const spent = now - this.#shownAt;
      // A tab left open overnight is not thinking time.
      if (spent > 0 && spent < 10 * 60 * 1000) {
        this.#elapsed[this.#shownId] = (this.#elapsed[this.#shownId] ?? 0) + spent;
      }
    }
    this.#shownId = questionId;
    this.#shownAt = questionId === null ? null : now;
  }

  private answerPayload(): {
    quiz_question_id: number;
    answer: string | null;
    elapsed_ms?: number;
  }[] {
    const given = this.answers();
    return this.questions().map((q) => ({
      quiz_question_id: q.id,
      answer: given[q.id] ?? null,
      elapsed_ms: this.#elapsed[q.id],
    }));
  }

  goTo(i: number): void {
    if (i < 0 || i >= this.total()) return;
    this.reviewing.set(false);
    this.index.set(i);
    this.markSeen(this.current()?.id ?? null);
  }

  advance(): void {
    // The last "next" opens the review screen rather than submitting outright —
    // submitting is final, so it gets its own deliberate press.
    if (this.index() >= this.total() - 1) {
      this.reviewing.set(true);
      this.markSeen(null);
      return;
    }
    this.index.update((i) => i + 1);
    this.markSeen(this.current()?.id ?? null);
  }

  back(): void {
    if (this.reviewing()) {
      this.reviewing.set(false);
      return;
    }
    this.index.update((i) => Math.max(0, i - 1));
    this.markSeen(this.current()?.id ?? null);
  }

  submit(): void {
    const attempt = this.attempt();
    if (!attempt || this.isSubmitting()) return;

    // A queued autosave would land after the paper is already graded, where the
    // API rightly refuses it — drop it rather than let it fire.
    if (this.#saveTimer) clearTimeout(this.#saveTimer);
    // Close the clock on the question still on screen before reading the totals.
    this.markSeen(null);
    const payload = this.answerPayload();

    this.isSubmitting.set(true);
    this.#quizService.submitAttempt(attempt.id, payload).subscribe({
      next: (result) => {
        this.isSubmitting.set(false);
        this.#router.navigate(["/online-class/quiz/result", result.id]);
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.#toastService.toastError(
          err?.error?.errors || "ส่งคำตอบไม่สำเร็จ",
        );
      },
    });
  }

  ngOnDestroy(): void {
    if (this.#saveTimer) clearTimeout(this.#saveTimer);
  }
}
